import React, { useState, useEffect, useRef } from 'react';
import * as CorgeeHelper from '@lib/corgeeHelper';
import type { Attachment } from '../../types/conversation';
import type { ApiConfig, AppConfig, CorgeeFunctions } from './types';
import { ImagesGallery, type Attachment as GalleryAttachment } from '@components/ImagesGallery';
import VideoModal from '@components/Globals/VideoModal';
import './AttachmentsContent.css';
import PdfModal from '@components/Globals/PdfModal';
import { useModalComponents } from '@contexts/PdfModalContext';

// Converts a base64 string to a Blob (mirrors legacy base64ToBlob).
function base64ToBlob(base64Data: string, contentType: string): Blob
{
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const byteArrays: Uint8Array[] = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize)
  {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const bytes = new Uint8Array(slice.length);
    for (let i = 0; i < slice.length; i++) bytes[i] = slice.charCodeAt(i);
    byteArrays.push(bytes);
  }
  return new Blob(byteArrays as BlobPart[], { type: contentType || 'application/octet-stream' });
}

// FilesApi — mirrors legacy FilesApi.DownloadFile:
//   calls api/attachment/GetFile?attachmentId=X, receives base64 JSON body
//   + x-filename / content-type headers, converts to blob and downloads.
//   Falls back to opening fileUrl in a new tab if the API call fails.
const FilesApi = {
  DownloadFile: (
    attachmentId: number,
    embeddedImageId: number | null,
    filename: string,
    fileUrl: string,
    api: ApiConfig,
  ) =>
  {
    let urlApi = '';
    if (attachmentId > 0)
      urlApi = `api/attachment/GetFile?attachmentId=${attachmentId}`;
    else if (embeddedImageId && embeddedImageId > 0)
      urlApi = `api/EmbebbedImages/GetFile?attachmentId=${embeddedImageId}`;

    if (!urlApi)
    {
      window.open(fileUrl, '_blank');
      return;
    }

    const token = api?.token;
    const baseUrl = (api?.url || '').replace(/\/$/, '');
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(`${baseUrl}/${urlApi}`, { headers })
      .then(res =>
      {
        const serverFilename = res.headers.get('x-filename');
        const contentType = res.headers.get('content-type') || 'application/octet-stream';
        const downloadName = (serverFilename || filename).replace(/(\r\n|\n|\r)/gm, '');
        return res.text().then(data => ({ data, contentType, downloadName }));
      })
      .then(({ data, contentType, downloadName }) =>
      {
        const blob = base64ToBlob(JSON.parse(data), contentType);
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() =>
      {
        window.open(fileUrl, '_blank');
      });
  },
};

interface GalleryState {
  open: boolean;
  currentImageId: number | string;
  attachments: Attachment[];
}

interface ModalState {
  open: boolean;
  title: string;
  template: any;
  url: string;
  playing: boolean;
  controls: boolean;
  attachments: Attachment | Attachment[];
  onFileDownloadVideo:
    | ((
        e: React.MouseEvent,
        attachment: Attachment,
        api: ApiConfig,
        corgeeFunctions?: CorgeeFunctions
      ) => void)
    | string;
}

interface PdfModalState {
  open: boolean;
  title: string;
  template: any;
  url: string;
  playing: boolean;
  controls: boolean;
  attachment: Attachment | Attachment[];
  onFileDownload:
    | ((
        e: React.MouseEvent,
        attachment: Attachment,
        api: ApiConfig,
        corgeeFunctions?: CorgeeFunctions
      ) => void)
    | string;
}

interface AttachmentsContentProps {
  attachments?: Attachment[];
  config?: AppConfig;
  api: ApiConfig;
  item?: any;
  corgeeFunctions?: CorgeeFunctions;
  isSingleReply?: string;
  PdfModalComponent?: React.ComponentType<any>;
  VideoModalComponent?: React.ComponentType<any>;
}

const AttachmentsContent: React.FC<AttachmentsContentProps> = ({
  attachments = [],
  config,
  api,
  item,
  corgeeFunctions,
  isSingleReply,
  PdfModalComponent,
  VideoModalComponent,
}) =>
{
  // Use modal components from Context, then props, then defaults
  const { PdfModalComponent: ContextPdfModal, VideoModalComponent: ContextVideoModal } = useModalComponents();
  const PdfModalToUse = ContextPdfModal || PdfModalComponent || PdfModal;
  const VideoModalToUse = ContextVideoModal || VideoModalComponent || VideoModal;

  // Resolve file extension from available fields, falling back to extracting
  // from FileName when FileExtension and FileType are both null/undefined
  // (common for reply attachments returned by the InfoWindow API).
  const resolveFileExt = (attach: Attachment): string =>
  {
    if (attach.FileExtension) return attach.FileExtension;
    if (attach.FileType) return attach.FileType;
    const name = attach.FileName || '';
    const dot = name.lastIndexOf('.');
    return dot !== -1 ? name.slice(dot) : '';
  };

  // Resolve the human-readable filename for download, matching display logic.
  // Uses Description + extension when available; falls back to FileName from API.
  const resolveDownloadFilename = (attach: Attachment): string =>
  {
    if (attach.Description)
    {
      return attach.Description + (attach.FileExtension || resolveFileExt(attach));
    }
    return attach.FileName || 'download';
  };

  const [displayAllImages, setDisplayAllImages] = useState(false);
  const [attachmentImagesArr, setAttachmentImagesArr] = useState<Attachment[]>([]);
  const [gallery, setGallery] = useState<GalleryState>({
    open: false,
    currentImageId: 0,
    attachments: [],
  });
  const [modal, setModal] = useState<ModalState>({
    open: false,
    title: '',
    template: null,
    url: '',
    playing: true,
    controls: true,
    attachments: [],
    onFileDownloadVideo: '',
  });
  const [pdfModal, setPdfModal] = useState<PdfModalState>({
    open: false,
    title: '',
    template: null,
    url: '',
    playing: true,
    controls: true,
    attachment: [],
    onFileDownload: '',
  });

  const videoModalRef = useRef<any>(null);
  const pdfModalRef = useRef<any>(null);

  // Initialize attachment images on component mount
  useEffect(() =>
  {
    updateAttachmentImages(attachments);
  }, []);

  // Update attachment images when props change
  useEffect(() =>
  {
    if (attachments)
    {
      updateAttachmentImages(attachments);
    }
  }, [attachments]);

  const updateAttachmentImages = (attachs: Attachment[]) =>
  {
    const filtered = attachs
      ? attachs.filter(
        miniAttach =>
          CorgeeHelper.extInImageExtensions(resolveFileExt(miniAttach)) === true &&
            (miniAttach.Thumbnail || miniAttach.ThumbnailUrl),
      )
      : [];
    setAttachmentImagesArr(filtered);
  };

  const showMoreAttachedImages = () =>
  {
    setDisplayAllImages(!displayAllImages);
    blockSideMenu();
  };

  const blockSideMenu = () =>
  {
    // Prevent context menu on images using native events
    const cardContainer = document.querySelector('.card-content-container-height-listener');
    if (cardContainer)
    {
      cardContainer.addEventListener('contextmenu', e => e.preventDefault());
    }

    const attachmentImages = document.querySelectorAll('.liConversationCard .attachments-img');
    attachmentImages.forEach(img =>
    {
      img.addEventListener('contextmenu', e => e.preventDefault());
    });
  };

  const onOpenGallery = (e: React.MouseEvent<HTMLAnchorElement>) =>
  {
    e.preventDefault();

    const target = e.currentTarget as HTMLElement;
    const currentAttachmentId = target.getAttribute('data-id');

    setGallery({
      open: true,
      currentImageId: currentAttachmentId || 0,
      attachments: attachmentImagesArr,
    });

    if (corgeeFunctions && corgeeFunctions.onOpenGallery)
    {
      corgeeFunctions.onOpenGallery(e, attachmentImagesArr);
    }
  };

  const onFileDownload = (e: React.MouseEvent, attachment: Attachment) =>
  {
    e.preventDefault();

    const videoExtensions = ['.3gp', '.mp4', '.mov', '.ogg', '.webm', '.f4v', '.m4v', '.mkv'];
    const pdfExtension = ['.pdf'];
    const fileExt = resolveFileExt(attachment);

    if (videoExtensions.includes(fileExt))
    {
      setModal({
        open: true,
        title: '',
        template: '',
        url: attachment.FileUrl || '',
        playing: true,
        controls: true,
        attachments: attachment,
        onFileDownloadVideo: (
          e: React.MouseEvent,
          att: Attachment,
          api: ApiConfig,
          corgee?: CorgeeFunctions,
        ) =>
        {
          // Chat's legacy VideoModal invokes this as (AttachmentId, attachments) with no
          // event, so `e` may not be a real MouseEvent — guard before calling preventDefault.
          e?.preventDefault?.();
          FilesApi.DownloadFile(att.AttachmentId, null, resolveDownloadFilename(att), att.FileUrl || '', api);
          if (corgee && corgee.onFileDownload)
          {
            corgee.onFileDownload(e, att);
          }
        },
      });

      setTimeout(() =>
      {
        const videoElement = document.querySelector(
          '.corgee_com_global-modal_body video',
        ) as HTMLVideoElement;
        if (videoElement)
        {
          videoElement.setAttribute('disablepictureinpicture', '');
          videoElement.setAttribute('controlsList', 'nodownload');
        }
      }, 0);
    }
    else if (pdfExtension.includes(fileExt))
    {
      setPdfModal({
        open: true,
        title: '',
        template: '',
        url: attachment.FileUrl || '',
        playing: true,
        controls: true,
        attachment: attachment,
        onFileDownload: (
          e: React.MouseEvent,
          att: Attachment,
          api: ApiConfig,
          corgee?: CorgeeFunctions,
        ) =>
        {
          // Chat's legacy PdfModal invokes this as (AttachmentId, attachment) with no
          // event, so `e` may not be a real MouseEvent — guard before calling preventDefault.
          e?.preventDefault?.();
          FilesApi.DownloadFile(att.AttachmentId, null, resolveDownloadFilename(att), att.FileUrl || '', api);
          if (corgee && corgee.onFileDownload)
          {
            corgee.onFileDownload(e, att);
          }
        },
      });
    }
    else
    {
      FilesApi.DownloadFile(
        attachment.AttachmentId,
        null,
        resolveDownloadFilename(attachment),
        attachment.FileUrl || '',
        api,
      );
      if (corgeeFunctions && corgeeFunctions.onFileDownload)
      {
        corgeeFunctions.onFileDownload(e, attachment);
      }
    }
  };

  const onCloseGallery = () =>
  {
    setGallery({
      open: false,
      currentImageId: 0,
      attachments: [],
    });
  };

  const onCloseVideo = () =>
  {
    setModal({
      open: false,
      title: '',
      template: null,
      url: '',
      playing: true,
      controls: true,
      attachments: [],
      onFileDownloadVideo: '',
    });
  };

  const onClosePdf = () =>
  {
    setPdfModal({
      open: false,
      title: '',
      template: null,
      url: '',
      playing: true,
      controls: true,
      attachment: [],
      onFileDownload: '',
    });
  };

  const getFileBackgoundImage = (attach: Attachment): string =>
  {
    const fileExt = resolveFileExt(attach);
    let fileBackgoundImageUrl = '';
    const documentClassName = CorgeeHelper.getDocumentClassName(fileExt);

    switch (documentClassName)
    {
      case 'Pdf':
        fileBackgoundImageUrl = 'Images/icon-pdf.png';
        break;
      case 'Doc':
        fileBackgoundImageUrl = 'Images/icon-word.png';
        break;
      case 'Xls':
        fileBackgoundImageUrl = 'Images/icon-excel.png';
        break;
      case 'Ppt':
        fileBackgoundImageUrl = 'Images/icon-ppt.png';
        break;
      case 'Zip':
        fileBackgoundImageUrl = 'Images/icon-zip.png';
        break;
      case 'Rar':
        fileBackgoundImageUrl = 'Images/icon-rar.png';
        break;
      case 'Psd':
        fileBackgoundImageUrl = 'Images/icon-psd.png';
        break;
      case 'Ai':
        fileBackgoundImageUrl = 'Images/icon-ai.png';
        break;
      case 'Pur':
        fileBackgoundImageUrl = 'Images/icon-pur.png';
        break;
      case 'Vid':
        fileBackgoundImageUrl = 'Images/icon-video.png';
        break;
      default:
        fileBackgoundImageUrl = 'Images/icon-default.png';
        break;
    }

    const baseUrl = api.url.endsWith('/') ? api.url : `${api.url}/`;
    return baseUrl + fileBackgoundImageUrl;
  };

  // Filter non-image attachments
  const attachmentNoImagesArr = attachments
    ? attachments.filter(
      miniAttach => CorgeeHelper.extInImageExtensions(resolveFileExt(miniAttach)) === false,
    )
    : [];

  // Determine image count CSS class
  let imageCountClass = '';
  switch (attachmentImagesArr.length)
  {
    case 0:
    case 1:
      imageCountClass = 'amount-one';
      break;
    case 2:
      imageCountClass = 'amount-two';
      break;
    case 3:
      imageCountClass = 'amount-three';
      break;
    default:
      imageCountClass = 'amount-more';
      break;
  }

  // Render image link helper
  const getImageLinkAttach = (attach: Attachment, indexItem: number): React.ReactNode =>
  {
    if (indexItem < 3 || displayAllImages === true)
    {
      if (config)
      {
        const thumbnailUrl = attach.Thumbnail ? attach.Thumbnail.FileUrl : attach.ThumbnailUrl;
        const imageUrl = `${thumbnailUrl}${config.buildVersion ? '?' + config.buildVersion : ''}`;
        return (
          <li key={'sr-img-' + attach.FileName}>
            <a
              onClick={onOpenGallery}
              data-id={attach.AttachmentId}
              className="attachments-img"
              style={{ backgroundImage: `url(${imageUrl})` }}
            />
          </li>
        );
      }
      else
      {
        return null;
      }
    }
    else
    {
      return null;
    }
  };

  return (
    <div className="corgee_com_card-user-attachments">
      <div className="corgee_com_card-user-attachments-thumbnails">
        {attachmentImagesArr.length > 0 && (
          <div
            className={
              'corgee_com_card-user-attachments-image-container' +
              (isSingleReply === 'true' ? ' single-replay' : '')
            }
          >
            <ul className={'corgee_com_card-user-attachments-image-previews ' + imageCountClass}>
              {attachmentImagesArr.map((attach, atIndex) => getImageLinkAttach(attach, atIndex))}
            </ul>
            {attachmentImagesArr.length > 3 && (
              <div className="corgee_com_card-user-attachments-toggle-image-view">
                {displayAllImages === false && (
                  <span
                    onClick={showMoreAttachedImages}
                    className="corgee_com_card-user-attachments-toggle-image-btn "
                  >
                    {attachmentImagesArr.length - 3}+
                  </span>
                )}
                {displayAllImages === true && (
                  <span
                    onClick={showMoreAttachedImages}
                    className="corgee_com_card-user-attachments-toggle-image-btn "
                  >
                    <i className="fa fa-chevron-up" />
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        {attachmentNoImagesArr.length > 0 && (
          <ul className="corgee_com_card-user-attachments-file-previews">
            {attachmentNoImagesArr.map(attach =>
            {
              return (
                <li
                  id={attach.AttachmentId != null ? attach.AttachmentId.toString() : ''}
                  key={'sr-file-' + attach.FileName}
                >
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={e => onFileDownload(e, attach)}
                    style={{ backgroundImage: 'url(' + getFileBackgoundImage(attach) + ')' }}
                    className={
                      CorgeeHelper.getDocumentClassName(resolveFileExt(attach)) +
                      ' corgee_com_card-user-attachments-file'
                    }
                  >
                    <span>
                      {attach.Description
                        ? attach.Description + (attach.FileExtension || '')
                        : attach.FileName}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ImagesGallery
        open={gallery.open}
        currentImageId={
          typeof gallery.currentImageId === 'string'
            ? parseInt(gallery.currentImageId, 10) || 0
            : gallery.currentImageId
        }
        attachments={gallery.attachments.map(
          (att): GalleryAttachment => ({
            AttachmentId: att.AttachmentId,
            FileUrl: att.FileUrl || '',
            Thumbnail: { FileUrl: att.Thumbnail?.FileUrl || att.ThumbnailUrl || '' },
            FileName: att.FileName,
            FileExtension: att.FileExtension || att.FileType || '',
            Favorite: att.Favorite,
            Description: att.Description,
            Title: att.Title,
            // Add conversation context for default reply target
            ConversationId: item?.ConversationId,
            ConversationTitle: (item as any)?.Title,
            ProjectName: (item as any)?.ProjectName,
            ProjectId: item?.ProjectId,
          }),
        )}
        onClose={onCloseGallery}
      />

      <VideoModalToUse
        ref={videoModalRef}
        className=""
        openModal={modal.open}
        url={modal.url}
        playing={modal.playing}
        controls={modal.controls}
        attachments={modal.attachments}
        onFileDownloadVideo={modal.onFileDownloadVideo}
        title={modal.title}
        corgeeFunctions={corgeeFunctions}
        api={api}
        onClose={onCloseVideo}
        isDraggable={true}
        closeOnEscape={true}
        height={480}
        width={680}
        hasMaxHeight={true}
        allowSaveAndNewCheckBox={false}
        hideActionButtons={true}
      />

      <PdfModalToUse
        ref={pdfModalRef}
        className=""
        openModal={pdfModal.open}
        attachment={pdfModal.attachment}
        onFileDownload={pdfModal.onFileDownload}
        title={pdfModal.title}
        corgeeFunctions={corgeeFunctions}
        api={api}
        onClose={onClosePdf}
        isDraggable={true}
        closeOnEscape={true}
        height={750}
        width="190vh"
        hasMaxHeight={false}
        allowSaveAndNewCheckBox={false}
        hideActionButtons={true}
      />
    </div>
  );
};

export default AttachmentsContent;
